/**
 * FmPropertyBoundarySnapshot — a static, framed snapshot of a property's REAL
 * boundary/stand geometry, keyed only by `propertyId`.
 *
 * Data-only, sealed API: the caller passes the property id (the data/registry key)
 * plus readable caption/aria text — never map JSX, tiles, geometry, or dimensions.
 * The primitive resolves the route-owned snapshot to the canonical FmStandMap and
 * frames it inside the sanctioned FmMapSnapshot media
 * canvas. Interactions are disabled (staticSnapshot) so the hero shows a calm "which
 * forest am I looking at" locator, not a live pannable map. No text is placed inside
 * the frame — captions live in the snapshot's figcaption chrome.
 *
 * NO FABRICATION: when the route reports the property's geometry as unavailable,
 * the primitive renders only that labelled state — never an empty map frame, which
 * reads as missing media — and it never invents a boundary.
 */
import React from "react";
import { FmBanner } from "./FmBanner";
import { FmSpinner } from "./FmFeedback";
import { FmMapSnapshot } from "@/shared/ui/FmMapStage";
import { FmStandMap } from "@/shared/ui/FmStandMap";
import { usePropertyForestSnapshot } from "@/contexts/usePropertyForestSnapshot";
import { PropertyBoundaryLayer } from "@/components/Map/ForestContextLayers";

export interface FmPropertyBoundarySnapshotProps {
  /** Property id used to select the route-owned immutable forest snapshot. */
  propertyId: string;
  /**
   * Readable caption text rendered in the snapshot figcaption chrome (outside the
   * frame). Plain string only — the sealed API exposes content, not JSX; any richer
   * caption composition stays inside DS-owned internals.
   */
  caption?: string;
  /** Semantic frame density — never a raw pixel height. */
  size?: "preview" | "compact" | "standard" | "tall";
  /** Immutable source revision used to remount Leaflet geometry in place. */
  revisionKey?: string;
  "data-testid"?: string;
}

/** Non-interactive map options for a calm, static hero locator. */
const STATIC_MAP_OPTIONS = {
  scrollWheelZoom: false,
  doubleClickZoom: false,
  dragging: false,
  zoomControl: false,
} as const;

const STATIC_INTERACTION = { click: false, hover: false } as const;

export const FmPropertyBoundarySnapshot: React.FC<
  FmPropertyBoundarySnapshotProps
> = ({
  propertyId,
  caption,
  size = "compact",
  revisionKey = "current",
  "data-testid": dataTestId,
}) => {
  const snapshot = usePropertyForestSnapshot(propertyId);
  // Retain an outage while refreshing; only a successfully loaded tile proves
  // recovery, not a new geometry response or a remounted map.
  const [rasterUnavailable, setRasterUnavailable] = React.useState(false);
  // Leaflet's GeoJSON layer updates styling, not its source geometry. Keep
  // both layers keyed to the actual published geometry, including refreshes.
  const geometryRevision = React.useMemo(
    () =>
      snapshot.revisionKey ?? JSON.stringify([
        revisionKey,
        snapshot.context?.standsGeoJson,
        snapshot.context?.propertyBoundaryGeoJson,
      ]),
    [snapshot.revisionKey, revisionKey, snapshot.context?.standsGeoJson, snapshot.context?.propertyBoundaryGeoJson],
  );
  if (!snapshot.isLoading && snapshot.error) {
    return <FmBanner tone="error" title={snapshot.error} data-testid={dataTestId} />;
  }
  const rasterError = rasterUnavailable ? snapshot.unavailableLabel : undefined;
  const content = snapshot.isLoading ? (
    <FmSpinner size="lg" />
  ) : (
    <FmStandMap
      key={`${propertyId}-${geometryRevision}`}
      geojson={snapshot.context?.standsGeoJson ?? null}
      standLayerKey={geometryRevision}
      bbox={snapshot.context?.bbox ?? null}
      coloringStrategy="species"
      interactionMode={STATIC_INTERACTION}
      mapOptions={STATIC_MAP_OPTIONS}
      basemap="maastokartta"
      // A whole-property locator is an overview zoom: the muted base lets the
      // stand colours and the boundary carry the frame.
      basemapTone="muted"
      onRasterUnavailableChange={setRasterUnavailable}
      fill
      testId={dataTestId ? `${dataTestId}-canvas` : undefined}
      mapChildren={() =>
        snapshot.context?.propertyBoundaryGeoJson ? (
          <PropertyBoundaryLayer
            data={snapshot.context.propertyBoundaryGeoJson}
            layerKey={`property-boundary-${propertyId}-${geometryRevision}`}
            testId="fm-property-boundary-layer"
          />
        ) : null
      }
    />
  );

  return (
    <FmMapSnapshot
      data-testid={dataTestId}
      size={size}
      aspectRatio="wide"
      variant="media"
      interactionMode="staticSnapshot"
      caption={caption}
      description={rasterError ? <FmBanner tone="error" title={rasterError} /> : undefined}
    >
      {content}
    </FmMapSnapshot>
  );
};

export default FmPropertyBoundarySnapshot;
